import { BaseApiService } from '@shared/_legacy/services/BaseApiService';
import { AxiosResponse } from 'axios';
import { singleton } from 'tsyringe';
import { IMemberResponseModel } from '@my-pages/features/auth/types/MemberResponseModel.ts';
import { IPersonResponseModel } from '@my-pages/features/membership/types/PersonResponseModel.ts';
import { IOrganizationUnitResponseModel } from '@my-pages/features/membership/types/OrganizationUnitResponseModel.ts';
import { IAssignment } from '@my-pages/features/auth/types/Assignment.ts';
import { IAssignmentV2Model } from '@my-pages/features/auth/types/AssignmentV2Model.ts';
import { IEmployment } from '@my-pages/features/employment/types/Employment.ts';
import { IEmploymentV2Model } from '@my-pages/features/employment/types/EmploymentV2Model.ts';
import { IMemberBooking } from '@my-pages/features/courses/types';
import { IClubMember } from '@my-pages/features/auth/types/ClubMember.ts';

@singleton()
export class MemberApiService extends BaseApiService {
    constructor() {
        super();
    }

    public get = (sessionId: any, options?: IBaseServiceOptions): Promise<AxiosResponse<IMemberResponseModel>> => {
        const request: IBaseServiceRequest = {
            url: `api/v1/member/${encodeURIComponent(sessionId)}`,
            model: null,
            options: options,
        };
        return this.doGet(request);
    };

    public getV2 = (sessionId: string, options?: IBaseServiceOptions): Promise<AxiosResponse<IPersonResponseModel>> => {
        const request: IBaseServiceRequest = {
            url: `api/v1/member/v2/${encodeURIComponent(sessionId)}`,
            model: null,
            options: options,
        };
        return this.doGet(request);
    };

    public post = (modelRequest: any, options?: IBaseServiceOptions): Promise<AxiosResponse<any>> => {
        const request: IBaseServiceRequest = {
            url: `api/v1/member`,
            model: modelRequest,
            options: options,
        };
        return this.doPost(request);
    };

    public put = (modelRequest: any, options?: IBaseServiceOptions): Promise<AxiosResponse<any>> => {
        const request: IBaseServiceRequest = {
            url: `api/v1/member`,
            model: modelRequest,
            options: options,
        };
        return this.doPut(request);
    };

    public getUnionForMember = (sessionId: any, options?: IBaseServiceOptions): Promise<AxiosResponse<IOrganizationUnitResponseModel>> => {
        const request: IBaseServiceRequest = {
            url: `api/v1/member/${encodeURIComponent(sessionId)}/union`,
            model: null,
            options: options,
        };
        return this.doGet(request);
    };

    public getAssignmentsForMember = (sessionId: any, options?: IBaseServiceOptions): Promise<AxiosResponse<IAssignment[]>> => {
        const request: IBaseServiceRequest = {
            url: `api/v1/member/${encodeURIComponent(sessionId)}/assignments`,
            model: null,
            options: options,
        };
        return this.doGet(request);
    };

    public getAssignmentsForMemberV2 = (sessionId: string, options?: IBaseServiceOptions): Promise<AxiosResponse<IAssignmentV2Model[]>> => {
        const request: IBaseServiceRequest = {
            url: `api/v1/member/v2/${encodeURIComponent(sessionId)}/assignments`,
            model: null,
            options: options,
        };
        return this.doGet(request);
    };

    public getEmploymentsForMember = (sessionId: any, options?: IBaseServiceOptions): Promise<AxiosResponse<IEmployment[]>> => {
        const request: IBaseServiceRequest = {
            url: `api/v1/member/${encodeURIComponent(sessionId)}/employments`,
            model: null,
            options: options,
        };
        return this.doGet(request);
    };

    public getPreviousEmploymentsForMember = (sessionId: any, options?: IBaseServiceOptions): Promise<AxiosResponse<IEmployment[]>> => {
        const request: IBaseServiceRequest = {
            url: `api/v1/member/${encodeURIComponent(sessionId)}/employments/previous`,
            model: null,
            options: options,
        };
        return this.doGet(request);
    };

    public getCurrentEmploymentsForMember = (sessionId: any, options?: IBaseServiceOptions): Promise<AxiosResponse<IEmployment[]>> => {
        const request: IBaseServiceRequest = {
            url: `api/v1/member/${encodeURIComponent(sessionId)}/employments/current`,
            model: null,
            options: options,
        };
        return this.doGet(request);
    };

    public getNextEmploymentsForMember = (sessionId: any, options?: IBaseServiceOptions): Promise<AxiosResponse<IEmployment[]>> => {
        const request: IBaseServiceRequest = {
            url: `api/v1/member/${encodeURIComponent(sessionId)}/employments/next`,
            model: null,
            options: options,
        };
        return this.doGet(request);
    };

    public getEmploymentsForMemberV2 = (sessionId: string, options?: IBaseServiceOptions): Promise<AxiosResponse<IEmploymentV2Model[]>> => {
        const request: IBaseServiceRequest = {
            url: `api/v1/member/v2/${encodeURIComponent(sessionId)}/employments`,
            model: null,
            options: options,
        };
        return this.doGet(request);
    };

    public updateMemberEmploymentAsync = (modelRequest: any, options?: IBaseServiceOptions): Promise<AxiosResponse<any>> => {
        const request: IBaseServiceRequest = {
            url: `api/v1/member/update-employment`,
            model: modelRequest,
            options: options,
        };
        return this.doPost(request);
    };

    public updateMemberNewEmploymentAsync = (modelRequest: any, options?: IBaseServiceOptions): Promise<AxiosResponse<any>> => {
        const request: IBaseServiceRequest = {
            url: `api/v1/member/update-new-employment`,
            model: modelRequest,
            options: options,
        };
        return this.doPost(request);
    };

    public getBookingsForMember = (sessionId: any, options?: IBaseServiceOptions): Promise<AxiosResponse<IMemberBooking[]>> => {
        const request: IBaseServiceRequest = {
            url: `api/v1/member/${encodeURIComponent(sessionId)}/bookings`,
            model: null,
            options: options,
        };
        return this.doGet(request);
    };

    public getClubMembers = (sessionId: any, page?: number, options?: IBaseServiceOptions): Promise<AxiosResponse<IClubMember[]>> => {
        const request: IBaseServiceRequest = {
            url: `api/v1/member/${encodeURIComponent(sessionId)}/club-members?page=${page ?? 1}`,
            model: null,
            options: options,
        };
        return this.doGet(request);
    };
}
